import React, { Component } from 'react';
import '../../ai-assistant.css';

class AiAssistantTray extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
            messages: [{
                role: 'bot',
                text: 'Hola, soy tu asistente inteligente. ¿Buscas algún servicio o negocio registrado hoy?',
                sources: []
            }],
            userInput: '',
            isLoading: false
        };
        this.chatBodyRef = React.createRef();
        this.inputRef = React.createRef();
        this.handleAiSearchEvent = this.handleAiSearchEvent.bind(this);
    }

    componentDidMount() {
        this.restoreState();
        window.addEventListener('aiSearch', this.handleAiSearchEvent);
    }

    restoreState = () => {
        try {
            const savedStateStr = sessionStorage.getItem('ai_chat_state');
            if (savedStateStr) {
                const savedState = JSON.parse(savedStateStr);
                const expiryTime = 10 * 60 * 1000; // 10 minutes

                if (Date.now() - savedState.timestamp < expiryTime) {
                    this.setState({
                        messages: savedState.messages || this.state.messages,
                        isOpen: false // Always start closed on page load, even if it was open before
                    });
                } else {
                    // State expired
                    sessionStorage.removeItem('ai_chat_state');
                }
            }
        } catch (e) {
            console.error("Error restoring AI chat state:", e);
        }
    }

    saveState = () => {
        try {
            const stateToSave = {
                messages: this.state.messages,
                isOpen: this.state.isOpen,
                timestamp: Date.now()
            };
            sessionStorage.setItem('ai_chat_state', JSON.stringify(stateToSave));
        } catch (e) {
            console.error("Error saving AI chat state:", e);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('aiSearch', this.handleAiSearchEvent);
    }

    handleAiSearchEvent(e) {
        const data = e.detail;

        let promptParts = [];
        if (data.text) promptParts.push(data.text);
        if (data.categoryName) promptParts.push(`en la categoría ${data.categoryName}`);
        if (data.cityName) promptParts.push(`en ${data.cityName}`);

        let prompt = promptParts.join(' ');
        if (!prompt) {
            prompt = "Recomiéndame algo";
        }

        // Open the tray if it's closed
        if (!this.state.isOpen) {
            this.setState({ isOpen: true }, () => {
                this.saveState();
                if (this.inputRef.current) {
                    setTimeout(() => {
                        if (this.inputRef.current) {
                            this.inputRef.current.focus();
                        }
                    }, 100);
                }
            });
        }

        // Trigger the search via the backend
        this.triggerSearch(prompt, data.text || '', data.categoryId, data.cityId);
    }

    toggleOpen = () => {
        this.setState(prevState => ({
            isOpen: !prevState.isOpen
        }), () => {
            this.saveState();
            if (this.state.isOpen && this.inputRef.current) {
                setTimeout(() => {
                    if (this.inputRef.current) {
                        this.inputRef.current.focus();
                    }
                }, 100);
            }
        });
    }

    handleInputChange = (e) => {
        this.setState({ userInput: e.target.value });
    }

    handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            this.handleSend();
        }
    }

    handleSend = () => {
        const { userInput } = this.state;
        if (!userInput.trim()) return;
        // Pass "" for keyword so the backend handles NLP tokenization for freestyle searches
        this.triggerSearch(userInput, "", null, null);
    }

    triggerSearch = (message, keyword, categoryId, cityId) => {
        // Add User Message
        this.addMessage('user', message);
        this.setState({ userInput: '', isLoading: true });

        // Dispatch an event to sync the top global filters if this was a freestyle search
        if (!keyword && !categoryId && !cityId) {
            window.dispatchEvent(new CustomEvent('aiSyncFilters', {
                detail: { text: message }
            }));
        }

        // Determine API URL based on environment
        const apiUrl = process.env.REACT_APP_ENV === 'live'
            ? '/v1/ai/chat'
            : '/dev/v1/ai/chat';

        // Call Backend
        fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                message: message,
                keyword: keyword,
                category_id: categoryId ? parseInt(categoryId) : 0,
                city_id: cityId ? parseInt(cityId) : 0
            })
        })
            .then(res => res.json())
            .then(data => {
                // Add Bot Message
                this.addMessage('bot', data.response, data.sources);

                // Dispatch event to update grids
                if (data.sources) {
                    window.dispatchEvent(new CustomEvent('updateSearchResults', {
                        detail: { sources: data.sources }
                    }));
                }
            })
            .catch(err => {
                console.error(err);
                this.addMessage('bot', 'Lo siento, tuve un problema conectando con el servidor.');
            })
            .finally(() => {
                this.setState({ isLoading: false });
            });
    }

    addMessage = (role, text, sources = []) => {
        this.setState(prevState => ({
            messages: [...prevState.messages, { role, text, sources }]
        }), () => {
            // Scroll to bottom
            if (this.chatBodyRef.current) {
                this.chatBodyRef.current.scrollTop = this.chatBodyRef.current.scrollHeight;
            }
            this.saveState();
        });
    }

    renderFormattedText = (text) => {
        // Simple regex to match Markdown links: [text](href)
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = linkRegex.exec(text)) !== null) {
            // Add text before the link
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }

            // Add the React link element
            parts.push(
                <a
                    key={match.index}
                    href={match[2]}
                    style={{ textDecoration: 'underline', color: '#007bff', fontWeight: 'bold', cursor: 'pointer' }}
                >
                    {match[1]}
                </a>
            );

            lastIndex = linkRegex.lastIndex;
        }

        // Add remaining text
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }

        return parts.length > 0 ? parts : text;
    };

    render() {
        const { isOpen, messages, userInput, isLoading } = this.state;
        const containerClass = isOpen ? 'ai-assistant-tray-container expanded' : 'ai-assistant-tray-container';

        return (
            <div className={containerClass}>
                {/* Expanded Panel */}
                <div className="ai-assistant-panel">
                    <div className="ai-chat-header">
                        <span style={{ fontWeight: 'bold', color: '#333' }}>Asistente La Guía Mundial</span>
                        <button onClick={(e) => { e.stopPropagation(); this.toggleOpen(); }} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '20px' }}>
                            &times;
                        </button>
                    </div>
                    <div className="ai-chat-body" ref={this.chatBodyRef}>
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`ai-message ${msg.role}`}>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{this.renderFormattedText(msg.text)}</div>
                            </div>
                        ))}
                        {isLoading && <div className="ai-message bot">...</div>}
                    </div>
                    <div className="ai-chat-input-area">
                        <input
                            type="text"
                            placeholder="Escribe tu consulta..."
                            value={userInput}
                            onChange={this.handleInputChange}
                            onKeyPress={this.handleKeyPress}
                            ref={this.inputRef}
                            autoFocus
                        />
                        <button onClick={this.handleSend}>Enviar</button>
                    </div>
                </div>

                <div className="ai-assistant-tray" onClick={this.toggleOpen}>
                    <div className="ai-orb"></div>
                    <div className="ai-tray-input-mock">
                        {isOpen ? 'Asistente Activo' : 'Pregúntame sobre estas ofertas...'}
                    </div>
                </div>
            </div>
        );
    }
}

export default AiAssistantTray;
